import { useContext, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { useGetData } from "../../../../../hooks/useGetData";
import { useMutationData } from "../../../../../hooks/useMutationData";
import { EvaluadoInterface } from "../../../../../interfaces/EvaluadoInterface";
import { FechasInterface } from "../../../../../interfaces/FechasInterface";
import { Loading } from "../../../../../components/Loading/Loading";
import { Label, TextInput, Button } from "flowbite-react";
import { API } from "../../../../../global/global";
import Header from "../../../../../components/HeaderForms/HeaderForms";
import { toast } from "react-toastify";
import { ThemeContext } from "../../../../../components/ThemeContext/ThemeContext";

export const FotosEditarScreen = () => {
  const { isDarkMode } = useContext(ThemeContext);
  const tituloFormulario = "EDITAR FOTOGRAFÍAS DE VISITA DOMICILIARIA";
  const { control, reset, handleSubmit, setValue } = useForm();
  const { evaluadosList_id } = useParams<{ evaluadosList_id: string }>();
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [images, setImages] = useState<(File | null)[]>([]);
  const [existingPhotos, setExistingPhotos] = useState<string[]>([]);
  const [fechaNacimiento, setFechaNacimiento] = useState<string>("");
  const [edad, setEdad] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const MAX_PHOTOS = 5;

  const [formData, setFormData] = useState({
    visitDate: "",
    name: "",
  });

  const calcularEdad = (fechaNacimiento: string) => {
    const fechaNac = new Date(fechaNacimiento);
    const hoy = new Date();
    let edadCalculada = hoy.getFullYear() - fechaNac.getFullYear();
    const mes = hoy.getMonth() - fechaNac.getMonth();

    if (mes < 0 || (mes === 0 && hoy.getDate() < fechaNac.getDate())) {
      edadCalculada--;
    }

    return edadCalculada;
  };

  useEffect(() => {
    const fetchData = async () => {
      const token = localStorage.getItem("token");
      try {
        const photosResponse = await fetch(
          `${API}/evaluado/${evaluadosList_id}/photos`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (photosResponse.ok) {
          const photosData = await photosResponse.json();
          setExistingPhotos(photosData.photos || []);
          setImages(Array(photosData.photos?.length || 0).fill(null));
        }

        const evaluadoResponse = await fetch(
          `${API}/evaluado/${evaluadosList_id}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (evaluadoResponse.ok) {
          const evaluadoData = await evaluadoResponse.json();
          const fechaNac = evaluadoData.fecha_nacimiento || "";

          reset({
            nombre: evaluadoData.nombre || "",
            apellido_paterno: evaluadoData.apellido_paterno || "",
            apellido_materno: evaluadoData.apellido_materno || "",
            rfc: evaluadoData.rfc || "",
            genero: evaluadoData.genero || "",
            estado_civil: evaluadoData.estado_civil || "",
            escolaridad: evaluadoData.escolaridad || "",
            fecha_nacimiento: fechaNac,
            curp: evaluadoData.curp || "",
            empresa: evaluadoData.empresa?.nombre || "",
          });
        }

        const fechaResponse = await fetch(
          `${API}/evaluado/${evaluadosList_id}/fecha-visita-bienesinmuebles`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (fechaResponse.ok) {
          const fechaData = await fechaResponse.json();
          if (fechaData.length > 0) {
            setValue("fecha_fotovd", fechaData[0].fecha_fotovd || "");
          }
        }
      } catch (error) {
        console.error("Error al obtener datos:", error);
        toast.error("Error al cargar los datos");
      }
    };

    if (evaluadosList_id) {
      fetchData();
    }
  }, [evaluadosList_id, reset, setValue]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const handleImageChange = (
    index: number,
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      const newImages = [...images];
      newImages[index] = file;
      setImages(newImages);
    }
  };

  const addImageBox = () => {
    if (images.length >= MAX_PHOTOS) {
      toast.warning(
        `No puedes agregar más fotos. Límite máximo de ${MAX_PHOTOS} fotos.`
      );
      return;
    }
    setImages([...images, null]);
  };

  const removeImageBox = (index: number) => {
    const newImages = [...images];
    newImages.splice(index, 1);

    const newExistingPhotos = [...existingPhotos];
    newExistingPhotos.splice(index, 1);

    setImages(newImages);
    setExistingPhotos(newExistingPhotos);
  };

  // Función actualizada para usar el nuevo endpoint de actualización de fotos
  const updatePhotos = async () => {
    const token = localStorage.getItem("token");
    setIsLoading(true);
    
    try {
      // Procesar cada imagen modificada utilizando el nuevo endpoint para actualizar individualmente
      const updatePromises = images
        .map(async (image, index) => {
          // Solo procesar las imágenes que han sido cambiadas (no nulas)
          if (image === null) return true;
          
          // Crear FormData para cada foto individual
          const formData = new FormData();
          formData.append("photo", image);
          
          // Usar el número de índice + 1 como número de foto
          const photoNumber = index + 1;
          
          // Llamar al endpoint de actualización individual
          const response = await fetch(
            `${API}/evaluado/${evaluadosList_id}/photos/${photoNumber}`,
            {
              method: "POST",
              body: formData,
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );
          
          const data = await response.json();
          
          if (response.ok) {
            return true;
          } else {
            toast.error(`Error al actualizar la foto ${photoNumber}: ${data.message}`);
            return false;
          }
        })
        .filter(Boolean); // Eliminar las promesas nulas (imágenes no modificadas)
      
      // Esperar a que todas las actualizaciones se completen
      const results = await Promise.all(updatePromises);
      
      // Verificar si todas las actualizaciones fueron exitosas
      if (results.every(result => result === true)) {
        toast.success("Fotos actualizadas exitosamente");
        return true;
      } else {
        toast.error("Hubo errores al actualizar algunas fotos");
        return false;
      }
    } catch (error) {
      console.error("Error en la actualización de fotos:", error);
      toast.error("Error de conexión al actualizar las fotos");
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const sendPhotosToBackend = async (photos: File[]) => {
    const token = localStorage.getItem("token");
    const formData = new FormData();

    // Agregar todas las fotos al FormData
    photos.forEach((photo, index) => {
      formData.append(`photos[${index}]`, photo);
    });

    try {
      setIsLoading(true);
      const response = await fetch(
        `${API}/evaluado/${evaluadosList_id}/photos`,
        {
          method: "POST",
          body: formData,
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data = await response.json();

      if (response.ok) {
        toast.success(data.message || "Fotos actualizadas exitosamente");
        return true;
      } else {
        toast.error(data.message || "Error al actualizar las fotos");
        return false;
      }
    } catch (error) {
      console.error("Error en la petición", error);
      toast.error("Error de conexión al actualizar las fotos");
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const generoMap: { [key: string]: string } = {
    M: "Masculino",
    F: "Femenino",
    O: "Otro",
  };

  const generoTexto =
    generoMap[
      useGetData<EvaluadoInterface>({
        url: `/evaluado/${evaluadosList_id}`,
        name: ["evaluado"],
      }).data?.genero as string
    ] || "No especificado";

  const queryEvaluado = useGetData<EvaluadoInterface>({
    url: `/evaluado/${evaluadosList_id}`,
    name: ["evaluado"],
  });

  const mutateFecha = useMutationData({
    url: `/evaluado/${evaluadosList_id}/fecha-visita-bienesinmuebles`,
    reload: ["evaluado", `${evaluadosList_id}`, "fecha-visita-bienesinmuebles"],
  });

  const queryFecha = useGetData<FechasInterface[]>({
    url: `/evaluado/${evaluadosList_id}/fecha-visita-bienesinmuebles`,
    name: ["fecha-visita-bienesinmuebles"],
  });

  useEffect(() => {
    if (queryEvaluado.data) {
      const fechaNac = queryEvaluado.data.fecha_nacimiento || "";
      setFechaNacimiento(fechaNac);

      const nuevaEdad = calcularEdad(fechaNac);
      console.log("Fecha de nacimiento:", fechaNac);
      console.log("Edad calculada:", nuevaEdad);
      setEdad(nuevaEdad);

      reset({
        nombre: queryEvaluado.data.nombre || "",
        apellido_paterno: queryEvaluado.data.apellido_paterno || "",
        apellido_materno: queryEvaluado.data.apellido_materno || "",
        rfc: queryEvaluado.data.rfc || "",
        genero: queryEvaluado.data.genero || "",
        estado_civil: queryEvaluado.data.estado_civil || "",
        escolaridad: queryEvaluado.data.escolaridad || "",
        fecha_nacimiento: fechaNac,
        curp: queryEvaluado.data.curp || "",
        empresa: queryEvaluado.data.empresa?.nombre || "",
      });
    }
  }, [queryEvaluado.data, reset]);

  useEffect(() => {
    console.log("queryFecha.data:", queryFecha.data);

    if (queryFecha.data && queryFecha.data.length > 0) {
      const registroValido = queryFecha.data.find(
        (item) => item.fecha_bienesinmuebles
      );

      if (registroValido) {
        setValue("fecha_bienesinmuebles", registroValido.fecha_bienesinmuebles);
      }
    }
  }, [queryFecha.data, setValue]);

  if (queryEvaluado.isLoading) {
    return <Loading />;
  }

  const renderPage1 = () => (
    <div className={`space-y-4 p-4 ${isDarkMode ? "bg-gray-800" : "bg-white"}`}>
      <div className="grid grid-cols-3 gap-4">
        {/* Fecha Visita */}
        <div className="col-span-1 flex items-center">
          <Label htmlFor="fechaVisita" className={`mr-2 ${isDarkMode ? "text-gray-300" : "text-gray-800"}`}>
            FECHA VISITA:
          </Label>
          <TextInput
            id="fechaVisita"
            name="fechaVisita"
            type="date"
            value={
              queryFecha.data && queryFecha.data.length > 0
                ? queryFecha.data[0].fecha_fotovd || ""
                : ""
            }
            onChange={handleInputChange}
            className={`w-32 ${isDarkMode ? "bg-gray-700 text-gray-300 border-gray-600" : "bg-white text-gray-800 border-gray-300"}`}
          />
        </div>
        {/* Campo para nombre */}
        <div className="col-span-2">
          <Label htmlFor="nombre" className={`${isDarkMode ? "text-gray-300" : "text-gray-800"}`}>NOMBRE:</Label>
          <TextInput
            id="nombre"
            name="nombre"
            value={`${queryEvaluado.data?.nombre || ""} ${
              queryEvaluado.data?.apellido_paterno || ""
            } ${queryEvaluado.data?.apellido_materno || ""}`}
            onChange={handleInputChange}
            disabled={true}
            className={`${isDarkMode ? "bg-gray-700 text-gray-300 border-gray-600" : "bg-gray-100 text-gray-800"}`}
          />
        </div>
        {/* Campo para RFC */}
        <div className="col-span-1">
          <Label htmlFor="rfc" className={`${isDarkMode ? "text-gray-300" : "text-gray-800"}`}>RFC:</Label>
          <TextInput
            id="rfc"
            name="rfc"
            onChange={handleInputChange}
            value={queryEvaluado.data?.rfc || ""}
            disabled={true}
            className={`${isDarkMode ? "bg-gray-700 text-gray-300 border-gray-600" : "bg-gray-100 text-gray-800"}`}
          />
        </div>
        {/* Edad */}
        <div className="col-span-1">
          <Label htmlFor="edad" className={`${isDarkMode ? "text-gray-300" : "text-gray-800"}`}>EDAD:</Label>
          <TextInput
            id="edad"
            name="edad"
            value={edad !== null ? edad.toString() : ""}
            disabled={true}
            className={`${isDarkMode ? "bg-gray-700 text-gray-300 border-gray-600" : "bg-gray-100 text-gray-800"}`}
          />
        </div>
        {/* Género */}
        <div className="col-span-1">
          <Label htmlFor="sexo" className={`${isDarkMode ? "text-gray-300" : "text-gray-800"}`}>SEXO:</Label>
          <TextInput
            id="sexo"
            name="sexo"
            onChange={handleInputChange}
            value={generoTexto}
            disabled={true}
            className={`${isDarkMode ? "bg-gray-700 text-gray-300 border-gray-600" : "bg-gray-100 text-gray-800"}`}
          />
        </div>
        {/* Estado Civil */}
        <div className="col-span-1">
          <Label htmlFor="estadoCivil" className={`${isDarkMode ? "text-gray-300" : "text-gray-800"}`}>ESTADO CIVIL:</Label>
          <TextInput
            id="estadoCivil"
            name="estadoCivil"
            onChange={handleInputChange}
            value={queryEvaluado.data?.estado_civil || ""}
            disabled={true}
            className={`${isDarkMode ? "bg-gray-700 text-gray-300 border-gray-600" : "bg-gray-100 text-gray-800"}`}
          />
        </div>
        {/* Escolaridad */}
        <div className="col-span-1">
          <Label htmlFor="escolaridad" className={`${isDarkMode ? "text-gray-300" : "text-gray-800"}`}>ESCOLARIDAD:</Label>
          <TextInput
            id="escolaridad"
            name="escolaridad"
            onChange={handleInputChange}
            value={queryEvaluado.data?.escolaridad || ""}
            disabled={true}
            className={`${isDarkMode ? "bg-gray-700 text-gray-300 border-gray-600" : "bg-gray-100 text-gray-800"}`}
          />
        </div>
        {/* Fecha de Nacimiento */}
        <div className="col-span-1 flex items-center">
          <Label htmlFor="fechaNacimiento" className={`mr-2 ${isDarkMode ? "text-gray-300" : "text-gray-800"}`}>
            FECHA DE NACIMIENTO:
          </Label>
          <TextInput
            id="fechaNacimiento"
            name="fechaNacimiento"
            value={queryEvaluado.data?.fecha_nacimiento || ""}
            disabled={true}
            type="date"
            className={`w-32 ${isDarkMode ? "bg-gray-700 text-gray-300 border-gray-600" : "bg-gray-100 text-gray-800"}`}
          />
        </div>
        {/* CURP */}
        <div className="col-span-1">
          <Label htmlFor="curp" className={`${isDarkMode ? "text-gray-300" : "text-gray-800"}`}>CURP:</Label>
          <TextInput
            id="curp"
            name="curp"
            onChange={handleInputChange}
            value={queryEvaluado.data?.curp || ""}
            disabled={true}
            className={`${isDarkMode ? "bg-gray-700 text-gray-300 border-gray-600" : "bg-gray-100 text-gray-800"}`}
          />
        </div>
        {/* Empresa */}
        <div className="col-span-1">
          <Label htmlFor="empresa" className={`${isDarkMode ? "text-gray-300" : "text-gray-800"}`}>EMPRESA:</Label>
          <TextInput
            id="empresa_id"
            name="empresa"
            value={queryEvaluado.data?.empresa_id?.nombre || ""}
            onChange={handleInputChange}
            disabled={true}
            className={`${isDarkMode ? "bg-gray-700 text-gray-300 border-gray-600" : "bg-gray-100 text-gray-800"}`}
          />
        </div>
      </div>
    </div>
  );

  const renderPage2 = () => (
    <div className={`space-y-6 p-6 rounded-lg shadow-md ${isDarkMode ? "bg-gray-800" : "bg-gray-50"}`}>
      {/* ... (otras partes del JSX permanecen igual) */}

      {Array.from({
        length: Math.max(images.length, existingPhotos.length),
      }).map((_, index) => {
        // Extraemos la imagen actual para manejar el tipo correctamente
        const currentImage = images[index];

        return (
          <div
            key={index}
            className={`relative flex flex-col items-center justify-center border-dashed border-4 rounded-lg p-8 mb-4 ${isDarkMode ? "border-gray-600 bg-gray-700" : "border-gray-300 bg-gray-100"}`}
          >
            <div className="text-center w-full">
              <p className={`mb-4 ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}>FOTOGRAFÍA {index + 1}</p>
              <input
                type="file"
                accept="image/*"
                className="hidden"
                id={`imageUpload-${index}`}
                onChange={(event) => handleImageChange(index, event)}
              />
              <label
                htmlFor={`imageUpload-${index}`}
                className={`cursor-pointer px-4 py-2 rounded inline-block mb-4 ${isDarkMode ? "bg-blue-700 text-white hover:bg-blue-800" : "bg-blue-600 text-white hover:bg-blue-700"}`}
              >
                {currentImage ? "Cambiar Imagen" : "Seleccionar Imagen"}
              </label>

              {/* Mostrar imagen existente o nueva */}
              {currentImage ? (
                <>
                  <div className="mt-2 w-full flex justify-center">
                    <img
                      src={URL.createObjectURL(currentImage)}
                      alt={`Nueva imagen ${index + 1}`}
                      className="max-w-full h-auto rounded max-h-48"
                    />
                  </div>
                  <p className={`mt-2 text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
                    {currentImage.name}
                  </p>
                </>
              ) : existingPhotos[index] ? (
                <div className="mt-2 w-full flex justify-center">
                  <img
                    src={existingPhotos[index]}
                    alt={`Imagen existente ${index + 1}`}
                    className="max-w-full h-auto rounded max-h-48"
                  />
                </div>
              ) : (
                <p className={`text-gray-500 ${isDarkMode ? "text-gray-400" : ""}`}>No hay foto seleccionada</p>
              )}

              {/* Botón para eliminar */}
              {(images[index] || existingPhotos[index]) && (
                <button
                  type="button"
                  onClick={() => removeImageBox(index)}
                  className={`absolute top-2 right-2 rounded-full w-6 h-6 flex items-center justify-center ${isDarkMode ? "bg-red-600 text-white hover:bg-red-700" : "bg-red-500 text-white hover:bg-red-600"}`}
                  aria-label="Eliminar imagen"
                >
                  ×
                </button>
              )}
            </div>
          </div>
        );
      })}

      {images.length +
        existingPhotos.filter((_, i) => i >= images.length).length <
        MAX_PHOTOS && (
        <div className="flex justify-center">
          <button
            type="button"
            onClick={addImageBox}
            className={`px-4 py-2 rounded flex items-center ${isDarkMode ? "bg-green-700 text-white hover:bg-green-800" : "bg-green-600 text-white hover:bg-green-700"}`}
          >
            <span className="mr-2">+</span> Agregar otra foto
          </button>
        </div>
      )}

      <div className={`mt-4 p-4 border-t ${isDarkMode ? "border-gray-600" : "border-gray-300"}`}>
        <div className="text-center">
          <p className={`text-left text-sm ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}>
            Bajo protesta de decir verdad, hago constar que los datos
            proporcionados son verdaderos.
          </p>
          <p className={`text-left text-sm ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}>
            Asimismo, autorizo a la empresa SKA TRACKING AND SECURITY S.A. de
            C.V. para que proporcione toda la Información, documentos y todos
            los datos que derive de las evaluaciones de pruebas de confianza que
            se me realicen.
          </p>
        </div>
      </div>
    </div>
  );

  const renderHeader = () => <Header titulo={tituloFormulario} />;

  return (
    <div className={`min-h-screen ${isDarkMode ? "bg-gray-900" : "bg-gray-50"}`}>
      <div className={`container mx-auto p-4`}>
        <form
          onSubmit={handleSubmit(async (data) => {
            const photosToUpload = images.filter(
              (img): img is File => img !== null
            );

            if (photosToUpload.length === 0 && existingPhotos.length === 0) {
              toast.warning("Debes tener al menos una foto");
              return;
            }

            if (photosToUpload.length + existingPhotos.length > MAX_PHOTOS) {
              toast.warning(`No puedes exceder el límite de ${MAX_PHOTOS} fotos`);
              return;
            }

            mutateFecha.mutate(
              {
                data: { fecha_fotovd: data.fecha_fotovd },
                tipo: "put",
              },
              {
                onSuccess: async () => {
                  // Si hay fotos para actualizar, usa el nuevo método de actualización
                  if (images.some(img => img !== null)) {
                    const updateSuccess = await updatePhotos();
                    if (updateSuccess) {
                      navigate(`/domiciliaria/${evaluadosList_id}`);
                    }
                  } else {
                    navigate(`/domiciliaria/${evaluadosList_id}`);
                  }
                },
                onError: (error) => {
                  console.error("Error al guardar fecha:", error);
                  toast.error("Error al guardar los datos");
                },
              }
            );
          })}
          className={`max-w-4xl mx-auto rounded-lg shadow-lg overflow-hidden ${isDarkMode ? "bg-gray-800" : "bg-white"}`}
        >
          {renderHeader()}

          {currentPage === 1 && renderPage1()}
          {currentPage === 2 && renderPage2()}

          <div className="mt-6 p-4 flex justify-between">
            {currentPage === 1 && (
              <Button
                color="red"
                onClick={() => navigate(`/domiciliaria/${evaluadosList_id}`)}
                className={`ml-2 ${isDarkMode ? "bg-red-600 hover:bg-red-700 text-white" : "bg-red-500 text-white"}`}
              >
                Cancelar
              </Button>
            )}

            {currentPage === 1 && (
              <Button
                color="blue"
                onClick={() => {
                  window.scrollTo(0, 0);
                  handlePageChange(currentPage + 1);
                }}
                className={`${isDarkMode ? "bg-blue-600 hover:bg-blue-700 text-white" : "bg-blue-500 text-white"}`}
              >
                Siguiente
              </Button>
            )}

            {currentPage > 1 && (
              <Button
                onClick={() => {
                  window.scrollTo(0, 0);
                  handlePageChange(currentPage - 1);
                }}
                className={`${isDarkMode ? "bg-violet-600 hover:bg-violet-900 text-white" : "bg-violet-400 text-white"}`}
              >
                Anterior
              </Button>
            )}

            {currentPage === 2 && (
              <div className="flex space-x-4">
                <Button type="submit" color="success" disabled={isLoading}
                  className={`${isDarkMode ? "bg-green-600 hover:bg-green-700 text-white" : "bg-green-500 text-white"}`}
                >
                  {isLoading ? "Guardando..." : "Guardar Cambios"}
                </Button>
              </div>
            )}
          </div>
        </form>
      </div>

    </div>
  );
};